import {LeanDocument, Types} from 'mongoose';
import {DeleteResponseDTO} from '@/classes/dto/delete.response.dto';
import {API, ApiRequestOptions} from './api.class';
import {CreateLogEntryRequestDto} from '@/classes/dto/recorder/request/CreateLogEntry.request.dto';
import {LogEntryTypes} from '@/enums/LogEntryEnums';
import {FindLogEntryRequestDto} from '@/classes/dto/recorder/request/FindLogEntry.request.dto';
import {UpdateLogEntryRequestDto} from '@/classes/dto/recorder/request/UpdateLogEntry.request.dto';
import {LogEntryDocument} from '@/documentTypes/recorder/LogEntry.document';
import {GetDistinctLogEntryTagsRequestDto} from '@/classes/dto/recorder/request/GetDistinctLogEntryTags.request.dto';
import {Tag} from '@/interfaces/tag.interface';

/** Tag with the number of log entries using it (`distinct-tags?withCounts=true`) */
export type LogEntryTagCount = Pick<Tag, '_id' | 'name' | 'color'> & { count?: number };
import {FindByIdOptionsRequestDto} from '@/classes/dto/_common/request/FindByIdOptions.request.dto';
import {
    GetStudentAttendancesRequestDto,
} from '@/classes/dto/recorder/request/aggregation/GetStudentAttendances.request.dto';
import {
    GetStudentAttendancesResponseDto,
} from '@/classes/dto/recorder/response/aggregation/GetStudentAttendances.response.dto';

/**
 * This class is used for communicating with Recorder microservice
 * @class Recorder
 * @author mklaczinski
 */
export class RecorderApi extends API {
    static service = 'recorder';

    static aggregation = {
        getStudentAttendances: async (data: GetStudentAttendancesRequestDto): Promise<GetStudentAttendancesResponseDto> => {
            return this.get('aggregation/student-attendances', data);
        },
    }

    static async getDistinctLogEntryTags(data: GetDistinctLogEntryTagsRequestDto): Promise<Types.ObjectId[] | Tag[]> {
        return RecorderApi.get('distinct-tags', data);
    }

    /**
     * Used tags including the number of entries per tag. Older recorder versions ignore `withCounts` and return the
     * populated tags without `count`, which is used for feature detection (see recorderFeatures.helper.ts).
     */
    static async getDistinctLogEntryTagsWithCounts(data: Omit<GetDistinctLogEntryTagsRequestDto, 'withCounts' | 'populate'>): Promise<LogEntryTagCount[]> {
        return RecorderApi.get('distinct-tags', {...data, populate: true, withCounts: true});
    }

    static async create<T extends LogEntryTypes>(data: CreateLogEntryRequestDto<T>): Promise<LogEntryDocument> {
        data.payload.__type = data.type;
        return RecorderApi.post('', data);
    }

    static async findById<T extends LeanDocument<LogEntryDocument> = LeanDocument<LogEntryDocument>>(id: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<T> {
        return RecorderApi.get(`${id}`, options);
    }

    /** Entries sorted with pinned (`sticky`) entries first, then by `sortBy`; `options` e.g. for silent requests */
    static async find<T extends LeanDocument<LogEntryDocument> = LeanDocument<LogEntryDocument>>(data: FindLogEntryRequestDto, options?: ApiRequestOptions): Promise<{
        logEntries: T[];
        maxPage: number
    }> {
        return RecorderApi.get('', data, undefined, options);
    }

    static async update(id: Types.ObjectId, data: UpdateLogEntryRequestDto): Promise<LogEntryDocument> {
        if (data.payload) {
            data.payload.__type = data.type;
        }
        return RecorderApi.put(`${id}`, data);
    }

    static async remove(id: Types.ObjectId): Promise<DeleteResponseDTO> {
        return RecorderApi.delete(`${id}`);
    }

    /** Restores a soft deleted log entry (newer recorder versions only, older ones answer with 404) */
    static async restore(id: Types.ObjectId | string): Promise<LogEntryDocument> {
        return RecorderApi.put(`restore/${id}`, {});
    }

}
